import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { fetchProfile } from "@/services/supabaseService";
import { Profile } from "@/types/supabase";
import { optimizedSupabaseImage } from "@/lib/utils";

const AboutMe = () => {
  const [displayedText, setDisplayedText] = useState("");
  const [isAnalyzed, setIsAnalyzed] = useState(false);
  const [analyzerPos1, setAnalyzerPos1] = useState({ x: 0, y: 0 });
  const [analyzerPos2, setAnalyzerPos2] = useState({ x: 0, y: 0 });
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentIndexRef = useRef(0);
  const positionIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const staticTextRef = useRef("");
  const typingTextRef = useRef("");

  const { data: profile } = useQuery<Profile | null>({
    queryKey: ["profile"],
    queryFn: fetchProfile,
  });

  const getBioParts = (bio?: string) => {
    if (!bio) return { staticPart: '', typingPart: '' };
    const text = bio.trim();
    const words = text.split(/\s+/);
    const tailCount = 50;
    const splitIndex = Math.max(words.length - tailCount, 0);
    const staticWords = words.slice(0, splitIndex);
    const typingWords = words.slice(splitIndex);
    const staticPart = staticWords.join(' ');
    const typingPart = typingWords.join(' ');
    return {
      staticPart: staticPart ? `${staticPart} ` : '',
      typingPart,
    };
  };

  const startAnalysis = () => {
    // Reset state
    setIsAnalyzed(false);
    currentIndexRef.current = 0;
    const { staticPart, typingPart } = getBioParts(profile?.about_me);
    staticTextRef.current = staticPart;
    typingTextRef.current = typingPart;
    setDisplayedText(staticPart);

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Start random position updates
    positionIntervalRef.current = setInterval(() => {
      setAnalyzerPos1({
        x: Math.random() * 100 - 50,
        y: Math.random() * 100 - 50
      });
      setAnalyzerPos2({
        x: Math.random() * 80 - 40,
        y: Math.random() * 80 - 40
      });
    }, 300);

    // Start typing animation
    intervalRef.current = setInterval(() => {
      if (currentIndexRef.current < typingTextRef.current.length) {
        setDisplayedText(
          staticTextRef.current +
            typingTextRef.current.substring(0, currentIndexRef.current + 1)
        );
        currentIndexRef.current++;
      } else {
        // Stop the animation after typing is complete
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        setIsAnalyzed(true);
      }
    }, 50);
  };

  useEffect(() => {
    if (profile) {
      startAnalysis();
    }

    return () => {
      if (positionIntervalRef.current) {
        clearInterval(positionIntervalRef.current);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  return (
    <div className="container max-w-6xl mx-auto py-10 px-6">
      <div className="animate-slide-down mb-6">
        <h2 className="text-4xl font-bold mb-2 text-teal">About Me</h2>
        <p className="text-gray-400">
          Learn more about who I am and what drives my passion for development.
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-[600px]">
        {/* Developer Themed Frame */}
        <div className="flex justify-center items-center h-full min-h-[480px]">
          <div className="relative w-full max-w-sm">
            {/* Analyzing Effect */}
            {(
              <div className="absolute -inset-2 bg-gradient-to-r from-emerald-500/20 via-transparent to-emerald-500/20 rounded-lg blur-lg animate-pulse pointer-events-none"></div>
            )}

            {/* Frame border */}
            <div className="relative bg-gradient-to-br from-emerald-600/20 to-emerald-900/20 rounded-lg p-1 border border-emerald-500/20 pulse-border">
              {/* Inner frame */}
              <div className="bg-gray-950 rounded-lg p-4 border border-emerald-500/30">

                {/* Image container */}
                <div className="relative overflow-hidden rounded-lg bg-gray-800 mb-0">
                  <img
                    src={optimizedSupabaseImage(profile?.profile_image_url, 768)}
                    alt={profile?.name || "Shishir Regmi"}
                    className="w-full h-auto aspect-square object-cover"
                    width="768"
                    height="768"
                    decoding="async"
                  />
                  
                  {/* Analyzing Effect - always visible, keeps moving */}
                  <div className="absolute inset-0 pointer-events-none">
                    {/* Scanning line */}
                    <div className="w-full h-1.5 bg-gradient-to-b from-emerald-400 via-emerald-300 to-transparent analyze-scan-line"></div>
                    
                    {/* Iron Man-style analyzer - Main scanner */}
                    <div 
                      className="absolute w-8 h-8 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                      style={{
                        transform: `translate(calc(-50% + ${analyzerPos1.x}px), calc(-50% + ${analyzerPos1.y}px))`,
                        transition: 'transform 0.3s ease-out'
                      }}
                    >
                      {/* Center point */}
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-emerald-400 rounded-full shadow-lg shadow-emerald-400/80"></div>
                      
                      {/* Crosshair lines */}
                      <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-emerald-400/60 to-transparent -translate-y-1/2"></div>
                      <div className="absolute top-0 left-1/2 w-0.5 h-full bg-gradient-to-b from-transparent via-emerald-400/60 to-transparent -translate-x-1/2"></div>
                      
                      {/* Pulsing ring */}
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 border border-emerald-400/50 rounded-full animate-pulse"></div>
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 border border-emerald-400/30 rounded-full animate-pulse" style={{ animationDelay: '0.3s' }}></div>
                    </div>
                    
                    {/* Secondary analyzer with different movement pattern */}
                    <div 
                      className="absolute w-6 h-6 border-2 border-emerald-300/40 rounded-full pointer-events-none"
                      style={{ 
                        top: '50%', 
                        left: '50%',
                        transform: `translate(calc(-50% + ${analyzerPos2.x}px), calc(-50% + ${analyzerPos2.y}px))`,
                        transition: 'transform 0.3s ease-out'
                      }}
                    ></div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>

        {/* AI Text Analyzer */}
        <div className="space-y-6">
          <div className="h-full">
            {/* Analyzer Card */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-950 rounded-xl border border-emerald-500/20 shadow-xl shadow-emerald-500/10 p-6 space-y-4 flex flex-col h-full">
              {/* Header with AI icon */}
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/10 rounded-lg">
                  <Sparkles className="h-5 w-5 text-emerald-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">
                    AI Analysis
                  </h3>
                  <p className="text-xs text-gray-400">Profile Intelligence</p>
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-gradient-to-r from-emerald-500/0 via-emerald-500/20 to-emerald-500/0"></div>

              {/* Text display with cursor - grows to fill available space */}
              <div className="bg-gray-950 rounded-lg border border-emerald-500/10 p-5 flex-1 overflow-y-auto">
                <p className="text-gray-300 text-sm leading-relaxed">
                  {displayedText}
                  <span className="animate-pulse ml-1 inline-block bg-emerald-400 h-4 w-0.5"></span>
                </p>
              </div>

              {/* Button - Clickable to restart analysis */}
              <button 
                onClick={startAnalysis}
                disabled={!isAnalyzed}
                className={`w-full rounded-md py-2 px-4 flex items-center justify-center gap-2 transition-colors duration-200 ${
                  isAnalyzed 
                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30 cursor-pointer' 
                    : 'bg-emerald-500/10 text-emerald-400/60 border border-emerald-500/20 cursor-not-allowed opacity-60'
                }`}
              >
                <div className={`h-2 w-2 bg-emerald-400 rounded-full ${!isAnalyzed ? 'animate-bounce' : ''}`}></div>
                {isAnalyzed ? 'Analyzed' : 'Analyzing...'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutMe;
